/** EFFET : chargement de MediaPipe auto-hébergé (voir scripts/fetch-mediapipe.ts). */
import { FaceLandmarker, FilesetResolver, HandLandmarker } from '@mediapipe/tasks-vision';
import { DEFAULT_DELEGATE, type Delegate } from './delegate';
import type { Model } from './landmarkerProtocol';

// Type non exporté par le paquet : on le déduit.
type WasmFileset = Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>;

const BASE = `${import.meta.env.BASE_URL}mediapipe`;

let fileset: Promise<WasmFileset> | undefined;
const getFileset = (): Promise<WasmFileset> =>
  (fileset ??= FilesetResolver.forVisionTasks(`${BASE}/wasm`));

export type Landmarker<M extends Model> = M extends 'face' ? FaceLandmarker : HandLandmarker;

/** Un modèle MediaPipe, sur le GPU ou le processeur ; fil principal ou worker. */
export async function createLandmarker<M extends Model>(
  model: M,
  delegate: Delegate = DEFAULT_DELEGATE,
): Promise<Landmarker<M>> {
  const wasm = await getFileset();
  const landmarker =
    model === 'face'
      ? await FaceLandmarker.createFromOptions(wasm, {
          baseOptions: { modelAssetPath: `${BASE}/models/face_landmarker.task`, delegate },
          runningMode: 'VIDEO',
          numFaces: 1,
          outputFaceBlendshapes: true,
        })
      : await HandLandmarker.createFromOptions(wasm, {
          baseOptions: { modelAssetPath: `${BASE}/models/hand_landmarker.task`, delegate },
          runningMode: 'VIDEO',
          numHands: 1,
        });
  return landmarker as Landmarker<M>;
}

export const createFaceLandmarker = (delegate?: Delegate): Promise<FaceLandmarker> =>
  createLandmarker('face', delegate);

export const createHandLandmarker = (delegate?: Delegate): Promise<HandLandmarker> =>
  createLandmarker('hand', delegate);
